/* eslint-disable */
const createOnUniqueEvent = require('./onUniqueEvent');

/**
 * Consent string and info if user choice is given.
 * @typedef {object} ConsentManagerConsentStatus
 * @property {string} consentData consent string
 * @property {boolean} consentExists true if exists consent
 * @property {boolean} userChoiceExists true if user has chosen. This does not
 * signal consent, but only if the user made a choice.
 * @property {string} version API version
 */

/**
 * Handler for an event of ConsentManager
 * @callback ConsentManagerEventHandler
 * @param {string} eventname Name of the event
 * @param {object} cmpobject CMP object
 * @param {any} subtype Subtype
 * @returns {void}
 * @link https://help.consentmanager.net/books/cmp/page/cmp-events
 */

/**
 * Add a CMP Event listener
 * @param {string} eventName The name of the CMP event
 * @param {ConsentManagerEventHandler} cmpHandler The callable handler function
 */
function addCMPEventListener(eventName, cmpHandler) {
  window.__cmp('addEventListener', [eventName, cmpHandler, false], null);
}

/**
 * Remove a CMP Event listener
 * @param {string} eventName The name of the CMP event
 * @param {ConsentManagerEventHandler} cmpHandler The callable handler function
 */
function removeCMPEventListener(eventName, cmpHandler) {
  window.__cmp('removeEventListener', [eventName, cmpHandler, false], null);
}

/**
 * Get consent string and info if user choice is given.
 * @returns {ConsentManagerConsentStatus|null} status
 */
function getCMPConsentStatus() {
  let result = null;
  if ('__cmp' in window) {
    result = window.__cmp('getCMPData')
  }
  return result ?? null;
}

/**
 * Get the data of CMP
 * @returns {object} data
 */
function getCMPData() {
  return window.__cmp('getCMPData');
}

/**
 * Check whether is general consent or for an specific vendor.
 * @param {object} [options] Options
 * @param {string} [options.vendorId] ID of the vendor
 * @returns {boolean} true if there is consent
 */
function existsConsent({ vendorId = null } = {}) {
  const data = getCMPData();
  /** @type {boolean} */
  let result = data.consentExists === true;
  if (result && vendorId !== null) {
    result = data?.vendorConsents?.[vendorId] ?? false;
  }
  return result;
}

/**
 * Check whether the user has chosen some settings
 * @returns {boolean} true if user has chosen
 */
function existsUserChoice() {
  const status = getCMPConsentStatus();
  return status?.userChoiceExists ?? false;
}

let foundUserChoiceInDatalayer = false;
/**
 * Check whether the event of a user has chosen some settings is already pushed
 * to dataLayer
 * @returns {boolean} true if user has chosen
 */
function existsUserChoiceInDatalayer() {
  /** @type {object[]} GA dataLayer array */
  const target = window?.dataLayer;
  let result = false;
  if (foundUserChoiceInDatalayer) {
    result = true;
  } else {
    result = target?.some((entry) => entry?.userChoiceExists === true) ?? false;
  }
  foundUserChoiceInDatalayer = result;
  return result;
}

let onReadyInterval;
/**
 * Call to a function when ConsentManager is ready
 * @param {function} arg0 Callback function
 */
const onCMPReady = createOnUniqueEvent()({
  addEventListener: (handler) => {
    onReadyInterval = setInterval(
      () => existsUserChoiceInDatalayer() && handler(),
      300
    );
  },
  removeEventListener: (_) => {
    clearInterval(onReadyInterval);
  },
  customState: {
    isFired: (state) => state,
    stateInitial: (_) => existsUserChoiceInDatalayer(),
    stateFired: (_) => true,
  },
});

/**
 * Send or enqueue an event for dataLayer
 * @param {object} gaevent GA event
 * @function
 */
const pushToDataLayer = (function cmpPushManager() {
  /**
   * This module is based on a functionality of CMP lib managed by:
   *  - cmp_gtag()
   *  - flushCMPDataLayer()
   *  - takeOverCMPDataLayer()
   *
   * Why?
   * ----
   * Because push to datalayer is different of the expected by gtag, though
   * gtag internally pushes to dataLayer cuase its signature is diferent.
   *
   * So, cpm_gtag pushes to dataLayer its arguments, but, as it is called as the
   * callback of a forEach, it is not called as a real gtag:
   * OK: gtag('event', '<event_name>', {<event_params>});
   * KO: gtag([eventObject]);
   *
   * @returns {function}
   */

  /** @type {object[]} Array of events for GA */
  let waitingPushes = [];
  let takeOverWaitingPushesDone = false;

  function flushWaitingPushes() {
    const numWaiting = waitingPushes.length;
    if (numWaiting > 0 && existsUserChoice()) {
      for (let i = 0; i < numWaiting; i++) {
        pushToDataLayerAfterUserChoice(waitingPushes[i]);
      }
      waitingPushes = [];
    }
  }

  function takeOverWaitingPushes() {
    if (!takeOverWaitingPushesDone && existsUserChoice()) {
      takeOverWaitingPushesDone = true;
      flushWaitingPushes();
      window.setInterval(flushWaitingPushes, 300);
    }
  }

  /**
   * Send or enqueue an event for dataLayer
   * @param {object} gaevent GA event
   */
  function pushToDataLayerAfterUserChoice(gaevent) {
    if (existsUserChoice()) {
      window.dataLayer.push(gaevent);
    } else {
      waitingPushes.push(gaevent);
    }
  }

  function initCMPPushManager() {
    if (existsUserChoiceInDatalayer()) {
      takeOverWaitingPushes();
    } else {
      onCMPReady(takeOverWaitingPushes);
    }
  }

  initCMPPushManager();
  return pushToDataLayerAfterUserChoice;
})();

module.exports = {
  addCMPEventListener,
  existsConsent,
  existsUserChoice,
  getCMPConsentStatus,
  getCMPData,
  onCMPReady,
  pushToDataLayer,
  removeCMPEventListener,
};
